// ============================================================================
// Projectile State - Client-side projectile tracking
// ============================================================================
// Manages active projectiles received from server for rendering.
// PERFORMANCE: Uses a plain mutable Map + version counter instead of signal
// replacement. The render layer reads the Map directly and uses the version
// counter for cache invalidation. No allocations per frame.

import type { ProjectileSnapshot } from '@dreamgame/shared';

// ============================================================================
// Types
// ============================================================================

// PERFORMANCE: Mutable interface - fields are updated in-place during interpolation
interface MutableProjectile {
  networkId: number;
  projectileType: number;
  sourceEntityId: number;
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  targetEntityId: number;
  speed: number;
  spawnTime: number;
}

export type ClientProjectile = Readonly<MutableProjectile>;

// ============================================================================
// State
// ============================================================================

// Plain Map, mutated directly. No signal wrapping — the render layer polls
// this via getAllProjectiles() and uses the version counter for caching.
const _projectiles = new Map<number, MutableProjectile>();

// Version counter bumped on structural changes (add/remove).
// Position-only updates during interpolation bump a separate counter so
// render layers can distinguish "need to rebuild sprite set" from "just moved".
let _version = 0;

export const getProjectilesVersion = (): number => _version;

// ============================================================================
// Cached array view
// ============================================================================
// PERFORMANCE: Reuse a single array for getAllProjectiles(). Rebuilt only when
// version changes. V8 keeps the backing store after .length = 0 so subsequent
// pushes don't allocate once the high-water mark is reached.

const _cachedArray: MutableProjectile[] = [];
let _cachedArrayVersion = -1;

export const getAllProjectiles = (): ReadonlyArray<ClientProjectile> => {
  if (_cachedArrayVersion === _version) {
    return _cachedArray;
  }
  _cachedArray.length = 0;
  for (const p of _projectiles.values()) {
    _cachedArray.push(p);
  }
  _cachedArrayVersion = _version;
  return _cachedArray;
};

// ============================================================================
// Actions
// ============================================================================

export const addProjectile = ({ snapshot }: { snapshot: ProjectileSnapshot }): void => {
  // Reuse existing object if present (e.g. rapid re-spawn on same id)
  let p = _projectiles.get(snapshot.networkId);
  if (!p) {
    p = {
      networkId: 0,
      projectileType: 0,
      sourceEntityId: 0,
      x: 0,
      y: 0,
      targetX: 0,
      targetY: 0,
      targetEntityId: 0,
      speed: 0,
      spawnTime: 0,
    };
    _projectiles.set(snapshot.networkId, p);
  }
  p.networkId = snapshot.networkId;
  p.projectileType = snapshot.projectileType;
  p.sourceEntityId = snapshot.sourceEntityId;
  p.x = snapshot.x;
  p.y = snapshot.y;
  p.targetX = snapshot.targetX;
  p.targetY = snapshot.targetY;
  p.targetEntityId = snapshot.targetEntityId;
  p.speed = snapshot.speed;
  p.spawnTime = performance.now();
  _version++;
};

export const removeProjectile = ({ networkId }: { networkId: number }): void => {
  if (_projectiles.delete(networkId)) {
    _version++;
  }
};

/**
 * Client-side position interpolation. Called each frame.
 * PERFORMANCE: Mutates x/y in place — zero allocations.
 */
export const updateProjectilePositions = ({ delta }: { delta: number }): void => {
  if (_projectiles.size === 0) {
    return;
  }

  const deltaSeconds = delta / 1000;
  let changed = false;

  for (const projectile of _projectiles.values()) {
    const dx = projectile.targetX - projectile.x;
    const dy = projectile.targetY - projectile.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 0.1) {
      continue;
    }

    const moveAmount = projectile.speed * deltaSeconds;
    if (moveAmount >= distance) {
      projectile.x = projectile.targetX;
      projectile.y = projectile.targetY;
    } else {
      projectile.x += (dx / distance) * moveAmount;
      projectile.y += (dy / distance) * moveAmount;
    }
    changed = true;
  }

  if (changed) {
    _version++;
  }
};

export const clearAllProjectiles = (): void => {
  _projectiles.clear();
  _version++;
};
